import React from 'react';
import P, { InferProps } from 'prop-types';
import { SxStyle } from '../../../types';
import Stack from '@mui/material/Stack';
import {
  Draggable,
  DraggableStateSnapshot,
  DraggingStyle,
  Droppable,
  NotDraggingStyle,
} from '@hello-pangea/dnd';
import ModuleClone from './module-clone';
import Card from '@mui/material/Card';
import ColumnHeader from './column-header';
import ColumnContent from './column-content';
import { Box, Button, Drawer } from '@mui/material';
import DrawerHeader from '../../common/drawer-header';

const card = {
  '& .MuiCardHeader-root': {
    padding: '10px',
  },
  marginTop: '6px',
  marginBottom: '6px',
  border: '1px dashed',
};
const styles: SxStyle = {
  card: (theme) => ({
    ...card,
    borderColor: theme.palette.primary.main,
    userSelect: 'none',
    backgroundColor: 'transparent',
  }),
  selectedCard: (theme) => ({
    ...card,
    borderColor: theme.palette.info.main,
    border: '2px solid',
    backgroundColor: 'transparent',
  }),
};

function StaticColumnNew({
  staticModules,
  // setDevices,
  // togglePreview,
  // selectedDevices,
  has1Column,
  isReadOnly,
  openStaticModulesDrawer,
  setOpenStaticModulesDrawer,
  addModule,
}: InferProps<typeof StaticColumnNew.propTypes>) {
  const [selectedModule, setSelectedModule] = React.useState<string | null>(null);
  const getStyle = (
    style: DraggingStyle | NotDraggingStyle | undefined,
    snapshot: DraggableStateSnapshot
  ) => {
    if (!snapshot.isDragging) {
      return {
        ...style,
        ...{ transform: 'none', backgroundColor: 'transparent' },
      };
    }

    return {
      ...style,
      backgroundColor: '#fff',
    };
  };

  const content = has1Column ? (
    <Box>
      {staticModules.map((module) => (
        <Box key={module.id}>
          <Card
            sx={selectedModule === module.id ? styles.selectedCard : styles.card}
            elevation={3}
            onClick={() => {
              setSelectedModule(module.id);
            }}
          >
            <ModuleClone title={module.name} />
          </Card>
        </Box>
      ))}
    </Box>
  ) : (
    <Droppable droppableId="MODULES" isDropDisabled>
      {(provided, snapshot) => (
        <Stack
          ref={provided.innerRef}
          sx={{
            py: 4,
          }}
        >
          {staticModules.map((module, index) => (
            <Draggable
              key={module.id}
              draggableId={module.id}
              index={index}
              isDragDisabled={isReadOnly}
            >
              {(provided, snapshot) => (
                <>
                  <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={styles.card}
                    style={getStyle(provided.draggableProps.style, snapshot)}
                    elevation={3}
                  >
                    <ModuleClone title={module.name} />
                  </Card>
                  {snapshot.isDragging && (
                    <Card sx={styles.card} style={{ opacity: 0.3 }} elevation={3}>
                      <ModuleClone title={module.name} />
                    </Card>
                  )}
                </>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </Stack>
      )}
    </Droppable>
  );

  const handleCloseDrawer = () => {
    setOpenStaticModulesDrawer(false);
    setSelectedModule(null);
  };

  if (has1Column) {
    return (
      <Drawer
        open={openStaticModulesDrawer}
        onClose={() => {
          handleCloseDrawer();
        }}
        anchor="right"
      >
        <DrawerHeader title="Tillgängliga moduler" onClose={handleCloseDrawer} />

        <Box
          sx={{
            height: 'calc(100vh - 100px)',
            px: 2,
            overflowY: 'auto',
            boxShadow: '0px 2px 5px 0px #0000001A',
          }}
        >
          {content}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 5, p: 2, background: '#fff' }}>
          <Button
            variant="outlined"
            color="info"
            fullWidth
            onClick={() => {
              handleCloseDrawer();
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            color="info"
            disabled={!selectedModule}
            fullWidth
            onClick={() => {
              addModule(selectedModule);
              handleCloseDrawer();
            }}
          >
            Lägg till
          </Button>
        </Box>
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        ...(has1Column ? { flex: '1 1 auto' } : {}),
      }}
    >
      <ColumnHeader title="Tillgängliga moduler" />
      {has1Column && openStaticModulesDrawer ? (
        <Box
          sx={{
            px: 5,
            pr: 8,
            py: 2,
            boxShadow: '0px 2px 5px 0px #0000001A',
          }}
        >
          <Button
            variant="outlined"
            size="small"
            color="info"
            fullWidth
            onClick={() => {
              setOpenStaticModulesDrawer(false);
            }}
          >
            Valda moduler
          </Button>
        </Box>
      ) : null}
      <ColumnContent>{content}</ColumnContent>
    </Box>
  );
}

StaticColumnNew.propTypes = {
  staticModules: P.arrayOf(P.any).isRequired,
  togglePreview: P.func.isRequired,
  setDevices: P.func.isRequired,
  selectedDevices: P.string.isRequired,
  isReadOnly: P.bool.isRequired,
  has1Column: P.bool.isRequired,
  openStaticModulesDrawer: P.bool.isRequired,
  setOpenStaticModulesDrawer: P.func.isRequired,
  addModule: P.func.isRequired,
};

export default StaticColumnNew;
